import { Button } from '@goodneighbor/ui';
import { UsersPlus } from '@goodneighbor/ui/icons/untitled';
import { type Event } from '@goodneighbor/api/schemas/events/event.schema';
import { useMemo } from 'react';
import useEventRegistrationCountQuery from '@/hooks/api/events/useEventRegistrationCountQuery';
import { isErrorResponse } from '@/services/typeguards';
import RegistrationCounter from '../RegistrationCounter';
import { cx } from 'class-variance-authority';
import {
  getRegisterButtonLabel,
  isRegisterButtonDisabled,
  isExternalEvent,
  getIsEventFull,
  getIsEventPast
} from '../../services/event';
import styles from './registerAndProgress.module.scss';

export type RegisterAndProgressProps = {
  event: Event;
  onRegister: () => void;
  showRegistrationCounter?: boolean;
  className?: string;
};

export default function RegisterAndProgress({
  event,
  onRegister,
  showRegistrationCounter = true,
  className
}: RegisterAndProgressProps) {
  const { data: registrationCountData } = useEventRegistrationCountQuery({
    enabled: showRegistrationCounter,
    id: event.id || ''
  });

  const registrationCount = useMemo(() => {
    if (
      registrationCountData &&
      !isErrorResponse(registrationCountData) &&
      registrationCountData.data
    ) {
      return registrationCountData.data.registrationCount;
    }
    return 0;
  }, [registrationCountData]);

  const isEventFull = useMemo(
    () => getIsEventFull(event, registrationCount),
    [event, registrationCount]
  );

  const isAlreadyRegistered = event?.registered ?? false;
  const isEventPast = getIsEventPast(event);

  const buttonState = { isAlreadyRegistered, isEventFull, isEventPast };

  return (
    <div className={cx(styles.registerAndProgress, className)}>
      <Button
        className={styles.registerButton}
        size="lg"
        color="primary"
        onClick={onRegister}
        isDisabled={isRegisterButtonDisabled(buttonState)}
      >
        {getRegisterButtonLabel(buttonState)}
      </Button>
      {showRegistrationCounter &&
        (isExternalEvent(event) ? (
          <div className={styles.volunteersRequested}>
            <UsersPlus
              width={16}
              height={16}
              className={styles.volunteersRequestedIcon}
            />
            <span>{`${event?.publicVolunteersCount ?? 0} volunteers requested`}</span>
          </div>
        ) : (
          <RegistrationCounter
            eventId={event?.id || ''}
            total={event?.overallCapacity || 0}
            className={styles.progressBar}
          />
        ))}
    </div>
  );
}
